import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UnicoCaptureResult } from '../../hooks/unico-context';
import {
  PrintStyle,
  PageContainer,
  PageHeader,
  PageHeaderRow,
  PageHeaderInfo,
  PageTag,
  PageTitle,
  PageSubtitle,
  ResultMainGrid,
  ResultDataColumn,
  ResultBottomGrid,
  MetadataGrid,
  ResultCard,
  ResultCardTitle,
  SelfieImage,
  SelfiePlaceholder,
  EncryptedBox,
  CopyButton,
  TokenBadge,
  TokenDetail,
  ProxyBadge,
  JsonBlock,
  ActionRow,
  ActionButton,
  EmptyStateWrapper,
  EmptyStateBox,
  MockFab,
} from './styles';

const RESULT_KEY = 'unico_test_result';

const MOCK_ENCRYPTED =
  'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJtb2NrLXByb2MtMjAyNDA0MDEtYWJjMTIzIiwiaWF0IjoxNzExOTQwODAwLCJleHAiOjE3MTE5NDQ0MDAsInByb3ZpZGVyIjoidW5pY28iLCJjYXB0dXJlX3R5cGUiOiJzZWxmaWVfbGl2ZW5lc3MifQ.MOCK_SIGNATURE_NOT_VALID';

const MOCK_RESULT: UnicoCaptureResult = {
  process_id: 'mock-proc-20240401-abc123',
  provider: 'unico',
  capture_type: 'selfie_liveness',
  encrypted: MOCK_ENCRYPTED,
  raw_base64: '',
  token: {
    issued_at: '2024-04-01T10:00:00.000Z',
    expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
    ttl_seconds: 600,
    is_expired: false,
  },
  proxy: {
    status: 'sent',
    dispatched_at: '2024-04-01T10:00:05.000Z',
    payload: {
      process_id: 'mock-proc-20240401-abc123',
      provider: 'unico',
      capture_type: 'selfie_liveness',
      encrypted: MOCK_ENCRYPTED,
      metadata: {
        captured_at: '2024-04-01T10:00:00.000Z',
        environment: 'UAT',
        source: 'web',
        user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148',
        process_persistence: 'session_storage',
      },
      token: {
        issued_at: '2024-04-01T10:00:00.000Z',
        expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
        ttl_seconds: 600,
        is_expired: false,
      },
      integrations: {
        serpro: { score_result: null, face_match_result: null, base_consulted: null, similarity_value: null },
        salesforce: { score_result: null, face_match_result: null, base_consulted: null, similarity_value: null },
      },
    },
  },
  metadata: {
    environment: 'UAT',
    source: 'web',
    captured_at: '2024-04-01T10:00:00.000Z',
    process_persistence: 'session_storage',
    user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148',
  },
};

const useCountdown = (expiresAt: string | null) => {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemaining(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return remaining;
};

export const ResultPage: React.FC = () => {
  const navigate = useNavigate();
  const [result, setResult] = useState<UnicoCaptureResult | null>(null);
  const [isMock, setIsMock] = useState(false);
  const [copied, setCopied] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RESULT_KEY);
      if (raw) setResult(JSON.parse(raw) as UnicoCaptureResult);
    } catch {
      // ignore parse errors
    }
  }, []);

  const remaining = useCountdown(result?.token?.expires_at ?? null);
  const isExpired = result?.token?.is_expired || (remaining !== null && remaining === 0);

  const handleCopy = async () => {
    if (!result?.encrypted) return;
    try {
      await navigator.clipboard.writeText(result.encrypted);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };

  const handleLoadMock = () => {
    setResult(MOCK_RESULT);
    setIsMock(true);
  };
  const handlePrint = () => window.print();
  const handleOpenNewTab = () => window.open('/test-unico/result', '_blank');
  const handleNewCapture = () => navigate('/test-unico/capture');
  const handleBackToSimulator = () => navigate('/test-unico');

  if (!result) {
    return (
      <PageContainer>
        <MockFab onClick={handleLoadMock}>
          <span>⚗</span> Ver exemplo
        </MockFab>
        <EmptyStateWrapper>
          <EmptyStateBox>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                fontSize: 24,
              }}
            >
              📋
            </div>

            <h2 className="heading-xl" style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>Nenhum resultado disponível</h2>

            <p style={{ fontSize: 14, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
              Realize uma captura pelo simulador para visualizar os resultados aqui.
            </p>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <ActionButton $variant="primary" onClick={handleNewCapture}>
                Iniciar captura
              </ActionButton>
              <ActionButton onClick={handleBackToSimulator}>Voltar ao simulador</ActionButton>
            </div>
          </EmptyStateBox>
        </EmptyStateWrapper>
      </PageContainer>
    );
  }

  const proxyStatus = result.proxy?.status ?? 'pending';
  const proxyLabels: Record<string, string> = {
    pending: 'Aguardando',
    sent: 'Enviado',
    failed: 'Falha no envio',
    skipped_expired: 'Ignorado (expirado)',
  };

  const formatSeconds = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  // Sanitize result for display — omit raw_base64 from JSON to keep it readable
  const displayResult = { ...result, raw_base64: '[omitted]' };

  return (
    <>
      <PrintStyle />
      <PageContainer>
        <MockFab onClick={handleLoadMock}>
          <span>⚗</span> {isMock ? 'Recarregar mock' : 'Ver exemplo'}
        </MockFab>
        <PageHeader style={{ maxWidth: 960, margin: '0 auto 20px' }}>
          <PageHeaderRow>
            <PageHeaderInfo>
              <PageTag>Test Unico{isMock ? ' · MOCK' : ''}</PageTag>
              <PageTitle>Resultado da captura</PageTitle>
              <PageSubtitle>
                Process ID: <code style={{ fontFamily: 'monospace', color: '#0284c7' }}>{result.process_id}</code>
                {' · '}
                {new Date(result.metadata?.captured_at ?? '').toLocaleString('pt-BR')}
              </PageSubtitle>
            </PageHeaderInfo>
            <ActionRow style={{ margin: 0 }}>
              <ActionButton $variant="primary" onClick={handlePrint}>
                Exportar PDF
              </ActionButton>
              <ActionButton onClick={handleOpenNewTab}>Nova aba</ActionButton>
              <ActionButton onClick={handleNewCapture}>Nova captura</ActionButton>
              <ActionButton onClick={handleBackToSimulator}>Simulador</ActionButton>
            </ActionRow>
          </PageHeaderRow>
        </PageHeader>

        <div id="unico-result-printable" ref={printableRef} style={{ maxWidth: 960, margin: '0 auto', flexDirection: 'column' }}>
          <ResultMainGrid>
            <ResultCard>
              <ResultCardTitle>Selfie capturada</ResultCardTitle>
              {result.raw_base64 ? (
                <SelfieImage src={`data:image/jpeg;base64,${result.raw_base64}`} alt="Selfie capturada" />
              ) : (
                <SelfiePlaceholder>Imagem não disponível</SelfiePlaceholder>
              )}
            </ResultCard>

            <ResultDataColumn>
              <ResultCard>
                <ResultCardTitle>Encrypted (JWT)</ResultCardTitle>
                <EncryptedBox title="Encrypted payload">{result.encrypted}</EncryptedBox>
                <CopyButton onClick={handleCopy}>{copied ? '✓ Copiado!' : 'Copiar encrypted'}</CopyButton>
              </ResultCard>

              <ResultCard>
                <ResultCardTitle>Token TTL</ResultCardTitle>
                <TokenBadge $expired={isExpired}>
                  {isExpired
                    ? 'Token expirado'
                    : remaining !== null
                    ? `Expira em ${formatSeconds(remaining)}`
                    : 'Verificando...'}
                </TokenBadge>
                <TokenDetail>Emitido: {new Date(result.token.issued_at).toLocaleString('pt-BR')}</TokenDetail>
                <TokenDetail>Expira: {new Date(result.token.expires_at).toLocaleString('pt-BR')}</TokenDetail>
                <TokenDetail>TTL: {result.token.ttl_seconds}s</TokenDetail>
              </ResultCard>

              <ResultCard>
                <ResultCardTitle>Proxy dispatch</ResultCardTitle>
                <ProxyBadge $status={proxyStatus}>{proxyLabels[proxyStatus] ?? proxyStatus}</ProxyBadge>
                {result.proxy?.dispatched_at && (
                  <TokenDetail>Enviado em: {new Date(result.proxy.dispatched_at).toLocaleString('pt-BR')}</TokenDetail>
                )}
              </ResultCard>
            </ResultDataColumn>
          </ResultMainGrid>

          <ResultBottomGrid>
            <ResultCard style={{ padding: 16 }}>
              <ResultCardTitle>Metadata</ResultCardTitle>
              <MetadataGrid>
                {[
                  ['Ambiente', result.metadata?.environment ?? '–'],
                  ['Source', result.metadata?.source ?? '–'],
                  ['Provider', result.provider],
                  ['Capture type', result.capture_type],
                  ['Persistência', result.metadata?.process_persistence ?? '–'],
                  ['User agent', result.metadata?.user_agent ?? '–'],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
                    <span style={{ fontSize: 13, color: '#0f172a', wordBreak: 'break-all' }}>{value}</span>
                  </div>
                ))}
              </MetadataGrid>
            </ResultCard>

            <ResultCard style={{ padding: 16 }}>
              <ResultCardTitle>JSON completo</ResultCardTitle>
              <JsonBlock>{JSON.stringify(displayResult, null, 2)}</JsonBlock>
            </ResultCard>
          </ResultBottomGrid>
        </div>
      </PageContainer>
    </>
  );
};
